// src/components/Control/ControlPanel/MapsPanel.tsx
// Сохранённые карты робота (~/maps на Pi): список с миниатюрами, открыть,
// удалить, новая карта, автосохранение.
//   robot/<id>/slam <- {"cmd": "list" | "new"} / {"cmd": "load" | "delete", "name"}
//   robot/<id>/maps -> {"maps": [{"name", "time", "auto", "loadable", "size", "thumb"}], "current"}
import React, { useEffect, useRef, useState } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';

interface MapEntry {
  name: string;
  time: number;
  auto: boolean;
  loadable: boolean;
  size: [number, number] | null;
  thumb: string | null;
}

interface Props {
  zenohSession: zenoh.Session;
  robotId: string;
  slamStatus: { ok: boolean | null; error?: string | null; loading?: boolean } | null;
  mapsSettings: { autosave_min: number; keep_auto: number } | null;
  onClose?: () => void;
}

const MapsPanel: React.FC<Props> = ({ zenohSession, robotId, slamStatus, mapsSettings, onClose }) => {
  const [maps, setMaps] = useState<MapEntry[] | null>(null);
  const [current, setCurrent] = useState<string | null>(null);
  const subRef = useRef<zenoh.Subscriber | null>(null);

  const slam = (cmd: object) =>
    zenohSession.put(`robot/${robotId}/slam`, JSON.stringify(cmd)).catch(err => console.warn('slam put failed', err));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await zenohSession.declareSubscriber(`robot/${robotId}/maps`, {
          handler: (sample: zenoh.Sample) => {
            try {
              const msg = JSON.parse(sample.payload().toString());
              setMaps(msg.maps);
              setCurrent(msg.current ?? null);
            } catch { /* ignore */ }
          },
        });
        if (cancelled) await s.undeclare();
        else {
          subRef.current = s;
          slam({ cmd: 'list' });  // the robot answers on robot/<id>/maps
        }
      } catch (err) {
        console.warn('MapsPanel: subscribe failed', err);
      }
    })();
    return () => {
      cancelled = true;
      subRef.current?.undeclare().catch(() => undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zenohSession, robotId]);

  const setAutosave = (patch: object) =>
    zenohSession.put(`robot/${robotId}/config`, JSON.stringify({ maps: patch })).catch(() => undefined);

  return (
    <div className="maps-panel" style={{ border: '1px solid #ddd', borderRadius: 8, padding: 8, margin: '6px 0', background: '#fafafa' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <b style={{ flex: 1 }}>Карты{current ? ` · открыта «${current}»` : ' · текущая — новая'}</b>
        <button onClick={() => { if (window.confirm('Начать новую карту? Текущая, если не сохранена, пропадёт.')) slam({ cmd: 'new' }); }}>
          Новая карта
        </button>
        <button onClick={() => slam({ cmd: 'list' })}>Обновить</button>
        {onClose && <button onClick={onClose} aria-label="Закрыть список карт">×</button>}
      </div>
      {mapsSettings && (
        <div style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          Автосохранение
          <select value={mapsSettings.autosave_min} onChange={e => setAutosave({ autosave_min: Number(e.target.value) })}>
            <option value={0}>выключено</option>
            {[2, 5, 10, 30, 60].map(m => <option key={m} value={m}>каждые {m} мин</option>)}
          </select>
          хранить
          <select value={mapsSettings.keep_auto} onChange={e => setAutosave({ keep_auto: Number(e.target.value) })}>
            {[3, 5, 10, 20].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          последних
        </div>
      )}
      {slamStatus?.loading && <div style={{ fontSize: 12, color: '#555' }}>Открываю карту…</div>}
      {slamStatus?.ok === false && slamStatus.error && <div style={{ fontSize: 12, color: '#c0392b' }}>Ошибка: {slamStatus.error}</div>}
      {maps === null ? (
        <div style={{ fontSize: 12, color: '#666' }}>Запрашиваю список у робота…</div>
      ) : maps.length === 0 ? (
        <div style={{ fontSize: 12, color: '#666' }}>Сохранённых карт нет</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 6, maxHeight: 360, overflowY: 'auto' }}>
          {maps.map(m => (
            <div key={m.name} style={{ border: m.name === current ? '2px solid #3498db' : '1px solid #ddd', borderRadius: 6, padding: 4, background: '#fff', fontSize: 11 }}>
              <div style={{ height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#cdcdcd' }}>
                {m.thumb ? <img src={`data:image/png;base64,${m.thumb}`} alt={m.name}
                                style={{ maxWidth: '100%', maxHeight: 90, imageRendering: 'pixelated' }} /> : 'нет картинки'}
              </div>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.name}>{m.name}</div>
              <div style={{ color: '#777' }}>{new Date(m.time).toLocaleString()}{m.auto ? ' · авто' : ''}</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                <button disabled={!m.loadable} title={m.loadable ? 'Продолжить эту карту: робот должен стоять там, где её начинали' : 'Нет графа поз — открыть нельзя'}
                        onClick={() => slam({ cmd: 'load', name: m.name })} style={{ flex: 1, fontSize: 11 }}>Открыть</button>
                <button onClick={() => { if (window.confirm(`Удалить карту «${m.name}»?`)) slam({ cmd: 'delete', name: m.name }); }}
                        style={{ fontSize: 11 }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapsPanel;
